import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home as HomeIcon, Heart as HeartIcon, ShoppingBag as ShoppingBagIcon, Truck as TruckIcon, ArrowLeft, Loader, Leaf, Shield, Award, CheckCircle2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL, resolveImageUrl } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './AboutUs.css';



const defaultAboutUs = {
  title: "About Himalayan Nuts",
  subtitle: "Our Story & Vision",
  story: "Sourcing the finest, premium quality nuts, dry fruits, and lotus seeds directly from the foothills of the Himalayas. We select only the highest grade harvests to ensure that every pack is filled with crunchy, delicious, and nutrient-dense superfoods.",
  image: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&q=80&w=600",
  values: [
    { title: "Pure Quality", description: "Every nut is sorted and graded to match premium criteria." },
    { title: "Direct Sourcing", description: "Harvested directly from farms in the pristine valleys." },
    { title: "Rich Nutrition", description: "Packed with vitamin E, fiber, and healthy oils." }
  ]
};

const AboutUs = () => {
  const navigate = useNavigate();
  const { wishlistCount, cartCount } = useCart();
  const [aboutData, setAboutData] = useState(defaultAboutUs);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/settings/about_us`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load About Us settings');
        return res.json();
      })
      .then((data) => {
        if (data && data.title) {
          setAboutData(data);
        }
      })
      .catch((err) => console.error('Error fetching about_us settings:', err))
      .finally(() => setLoading(false));
  }, []);

  // Map value index to standard icons
  const getValueIcon = (index) => {
    switch (index) {
      case 0:
        return <Award size={28} className="value-icon" />;
      case 1:
        return <Leaf size={28} className="value-icon" />;
      case 2:
        return <Shield size={28} className="value-icon" />;
      default:
        return <CheckCircle2 size={28} className="value-icon" />;
    }
  };

  return (
    <div className="about-page-wrapper">
      {/* Top Announcement Bar */}
      <TopBar />

      {/* Main Header */}
      <Header 
        wishlistCount={wishlistCount} 
        cartCount={cartCount} 
        onCartClick={() => navigate('/cart')} 
      />

      {/* Navigation Bar */}
      <Navbar activeItem="About Us" />

      {/* Main Content */}
      <main className="about-main">
        <div className="container">
          <div className="about-header-row">
            <Link to="/" className="back-btn">
              <ArrowLeft size={16} />
              <span>Back to Shop</span>
            </Link>
            <h1 className="about-page-title">{aboutData.title}</h1>
          </div>

          {loading ? (
            <div className="about-loader-container">
              <Loader className="spinner" size={40} />
              <p>Loading our story...</p>
            </div>
          ) : (
            <div className="about-content-section">
              {/* Story Intro */}
              <div className="about-grid">
                {/* Left Side: Story Text */}
                <div className="about-story-text">
                  <span className="about-motto">{aboutData.subtitle}</span>
                  <div className="story-paragraph-wrapper">
                    {aboutData.story.split('\n').map((para, idx) => (
                      <p key={idx} className="story-p">{para}</p>
                    ))}
                  </div>
                </div>

                {/* Right Side: Story Banner */}
                {aboutData.image && (
                  <div className="about-story-img-box">
                    <img 
                      src={resolveImageUrl(aboutData.image)} 
                      alt="Himalayannuts Harvest Story" 
                      className="about-story-img"
                    />
                  </div>
                )}
              </div>

              {/* Core Values Section */}
              <div className="about-values-section">
                <h2 className="about-values-title">Our Brand Core Values</h2>
                <div className="values-grid">
                  {aboutData.values && aboutData.values.map((val, index) => (
                    <div key={index} className="value-card">
                      <div className="value-icon-wrapper">
                        {getValueIcon(index)}
                      </div>
                      <h3>{val.title}</h3>
                      <p>{val.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Sticky Bottom Navigation (Mobile screens only) */}
      <div className="mobile-sticky-nav" role="navigation" aria-label="Mobile Navigation">
        <Link 
          to="/" 
          className="mobile-nav-item"
        >
          <HomeIcon size={20} />
          <span>Home</span>
        </Link>
        <Link 
          to="/track" 
          className="mobile-nav-item"
        >
          <TruckIcon size={20} />
          <span>Track Order</span>
        </Link>
        <Link 
          to="/wishlist" 
          className="mobile-nav-item"
        >
          <HeartIcon size={20} />
          {wishlistCount > 0 && (
            <span className="badge-count">{wishlistCount}</span>
          )}
          <span>Wishlist</span>
        </Link>
        <Link 
          to="/cart" 
          className="mobile-nav-item"
        >
          <ShoppingBagIcon size={20} />
          {cartCount > 0 && (
            <span className="badge-count">{cartCount}</span>
          )}
          <span>Cart</span>
        </Link>
      </div>
    </div>
  );
};

export default AboutUs;
